import {
  ReceivableBillTo,
  ReceivableRow,
  ReceivableStatus,
  ReceivableStatusFilter,
  ReceivableType,
} from '@bariendo/lib/receivables';
import dayjs from 'dayjs';
import { rowsToCsv } from '@/services/csv';

/** Receivables (BAR-3038): employer and insurance lines in one list, filtered and summarized in the browser. */

export const BILL_TO_LABEL: Record<ReceivableBillTo, string> = {
  CARRUM: 'Carrum',
  TRANSCARENT: 'Transcarent',
  MUSC_HEALTH: 'MUSC Health',
  SURGEONS_CHOICE: 'Surgeons Choice',
};

/** The summary's rows, in the prototype's order. */
export const BILL_TOS: { billTo: ReceivableBillTo; track: string }[] = [
  { billTo: 'CARRUM', track: 'Employer' },
  { billTo: 'TRANSCARENT', track: 'Employer' },
  { billTo: 'MUSC_HEALTH', track: 'Insurance' },
  { billTo: 'SURGEONS_CHOICE', track: 'Insurance' },
];

export const STATUS_LABEL: Record<ReceivableStatus, string> = {
  TO_INVOICE: 'To invoice',
  INVOICED: 'Invoiced',
  PAID: 'Paid',
};

export const STATUS_TONE: Record<ReceivableStatus, 'warning' | 'info' | 'success'> = {
  TO_INVOICE: 'warning',
  INVOICED: 'info',
  PAID: 'success',
};

export const FILTER_ALL = 'all';

export interface ReceivableFiltersState {
  track: 'all' | 'employer' | 'insurance';
  billTo: 'all' | ReceivableBillTo;
  type: 'all' | ReceivableType;
  status: ReceivableStatusFilter;
}

export const DEFAULT_RECEIVABLE_FILTERS: ReceivableFiltersState = {
  track: 'all',
  billTo: 'all',
  type: 'all',
  status: 'open',
};

const STATUSES_SHOWN: Record<ReceivableStatusFilter, ReceivableStatus[]> = {
  open: ['TO_INVOICE', 'INVOICED'],
  to_invoice: ['TO_INVOICE'],
  invoiced: ['INVOICED'],
  paid: ['PAID'],
  all: ['TO_INVOICE', 'INVOICED', 'PAID'],
};

const STATUS_RANK: Record<ReceivableStatus, number> = { TO_INVOICE: 0, INVOICED: 1, PAID: 2 };

/**
 * The tab's order: To invoice by oldest service date, Invoiced by oldest
 * invoice, Paid newest first.
 */
function compareReceivables(a: ReceivableRow, b: ReceivableRow): number {
  const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status];
  if (rank !== 0) return rank;
  if (a.status === 'PAID') return (b.paidOn ?? '').localeCompare(a.paidOn ?? '');
  if (a.status === 'INVOICED') return (a.invoicedOn ?? '').localeCompare(b.invoicedOn ?? '');
  return a.serviceDate.localeCompare(b.serviceDate);
}

export function filterReceivables(rows: ReceivableRow[], filters: ReceivableFiltersState): ReceivableRow[] {
  return rows
    .filter((row) => STATUSES_SHOWN[filters.status].includes(row.status))
    .filter((row) => filters.track === FILTER_ALL || row.track === filters.track)
    .filter((row) => filters.billTo === FILTER_ALL || row.billTo === filters.billTo)
    .filter((row) => filters.type === FILTER_ALL || row.type === filters.type)
    .sort(compareReceivables);
}

export function isFiltered(filters: ReceivableFiltersState): boolean {
  return (Object.keys(filters) as (keyof ReceivableFiltersState)[]).some(
    (key) => filters[key] !== DEFAULT_RECEIVABLE_FILTERS[key]
  );
}

/** What the Invoiced column shows: the invoiced amount once invoiced, else the amount to invoice. */
export function invoicedAmount(row: ReceivableRow): number | null {
  return row.invoicedAmount ?? row.amount;
}

/** `Oct 2, 2026`, from `YYYY-MM-DD`. */
export function formatReceivableDate(date: string | null): string {
  return date ? dayjs(date).format('MMM D, YYYY') : '';
}

export interface ReceivableSummaryRow {
  billTo: ReceivableBillTo | 'total';
  track: string;
  toInvoice: number;
  invoiced: number;
  paidThisMonth: number;
}

/** Bill to by status over every line, the filters aside; Paid counts only this month's payments. */
export function summarizeReceivables(rows: ReceivableRow[], now: Date): ReceivableSummaryRow[] {
  const month = dayjs(now).format('YYYY-MM');
  const sum = (lines: ReceivableRow[]) => ({
    toInvoice: lines.filter((row) => row.status === 'TO_INVOICE').reduce((total, row) => total + (row.amount ?? 0), 0),
    invoiced: lines
      .filter((row) => row.status === 'INVOICED')
      .reduce((total, row) => total + (invoicedAmount(row) ?? 0), 0),
    paidThisMonth: lines
      .filter((row) => row.status === 'PAID' && row.paidOn?.startsWith(month))
      .reduce((total, row) => total + (row.paidAmount ?? 0), 0),
  });
  return [
    ...BILL_TOS.map(({ billTo, track }) => ({
      billTo,
      track,
      ...sum(rows.filter((row) => row.billTo === billTo)),
    })),
    { billTo: 'total' as const, track: '', ...sum(rows) },
  ];
}

export const RECEIVABLE_CSV_HEADERS = [
  'Patient',
  'Track',
  'Bill to',
  'Type',
  'Service date',
  'Status',
  'Invoice date',
  'Invoiced',
  'Paid date',
  'Received',
  'Invoice number',
  'Bank reference',
  'Note',
];

const csvMoney = (value: number | null) => (value === null ? '' : value.toFixed(2));

export function receivablesCsv(rows: ReceivableRow[]): string {
  return rowsToCsv(
    RECEIVABLE_CSV_HEADERS,
    rows.map((row) => [
      row.patientName ?? '',
      row.track === 'employer' ? 'Employer' : 'Insurance',
      BILL_TO_LABEL[row.billTo],
      row.type === 'CONSULTATION_FEE' ? 'Consultation fee' : 'Procedure',
      row.serviceDate,
      STATUS_LABEL[row.status],
      row.invoicedOn ?? '',
      csvMoney(invoicedAmount(row)),
      row.paidOn ?? '',
      row.status === 'PAID' ? csvMoney(row.paidAmount) : '',
      row.invoiceNumber ?? '',
      row.bankReference ?? '',
      row.note ?? '',
    ])
  );
}

export function receivablesCsvFilename(now: Date): string {
  return `receivables-${dayjs(now).format('YYYY-MM-DD')}.csv`;
}
